'use client'
import { Cart } from '@/components/Cart'
import { OpenCartButton } from '@/components/Cart/OpenCart'
import { Link, usePathname } from '@/i18n/routing'
import React, { Suspense, useEffect, useRef, useState } from 'react'
import { SearchIcon, XIcon, UserIcon, LogOutIcon } from 'lucide-react'
import { Search } from '@/components/Search'
import { useAuth } from '@/providers/Auth'
import { LanguageToggle } from '@/components/LanguageToggle'
import { useTranslations } from 'next-intl'
import { AnnouncementBanner } from '@/components/AnnouncementBanner'
import { WishlistBadgeClient } from './WishlistBadgeClient'

import dynamic from 'next/dynamic'

const MobileMenu = dynamic(
  () => import('./MobileMenu').then((m) => ({ default: m.MobileMenu })),
  {
    ssr: false,
    loading: () => <div className="w-10 h-10" aria-hidden="true" />,
  },
)
import type { Header } from 'src/payload-types'

import { HeaderNav } from './HeaderNav'
import { AdminUserStats } from './AdminUserStats'
import Image from 'next/image'
import { useLocale } from 'next-intl'

type Props = {
  header: Header
  initialWishlistCount?: number
}

export function HeaderClient({ header, initialWishlistCount = 0 }: Props) {
  const t = useTranslations('common')
  const navT = useTranslations('navigation')
  const locale = useLocale()
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const { user, logout } = useAuth()
  const menu = header.navItems || []
  const headerRef = useRef<HTMLElement>(null)
  const pathname = usePathname()
  const isBlog = pathname === '/blog' || pathname.startsWith('/blog/')

  useEffect(() => {
    const el = headerRef.current
    if (!el) return
    const root = document.documentElement
    const set = () => {
      root.style.setProperty('--header-h', `${el.offsetHeight}px`)
      // Blog pages: header scrolls away, so nothing sticky needs to clear it.
      root.style.setProperty('--header-stick-h', isBlog ? '0px' : `${el.offsetHeight}px`)
    }
    set()
    const ro = new ResizeObserver(set)
    ro.observe(el)
    return () => ro.disconnect()
  }, [isBlog])

  const isAdmin = user?.roles && Array.isArray(user.roles) && user.roles.includes('admin')
  const showUserBar = user && !isAdmin

  return (
    <>
      <header ref={headerRef} className={`${isBlog ? 'relative' : 'sticky top-0'} z-50 w-full flex flex-col headerSafeArea`}>
        <div className="w-full bg-black/40 backdrop-blur-md border-b border-white/10 px-2 sm:px-4 lg:px-8 2xl:px-12">
          <div className="flex items-center justify-end gap-3 h-7 text-xs font-mono text-white/70">
            {/* Language group */}
            <div className="flex items-center flex-shrink-0">
              <LanguageToggle />
            </div>

            <div className="w-px h-3 bg-white/20 flex-shrink-0" aria-hidden="true" />

            {user === undefined ? (
              /* Session not known yet: invisible placeholder with the logged-out
                 footprint so nothing flashes or shifts. */
              <div className="flex items-center gap-3 invisible" aria-hidden="true">
                <span>{navT('login', { fallback: 'Login' })}</span>
                <span className="opacity-30">|</span>
                <span>{navT('register', { fallback: 'Register' })}</span>
              </div>
            ) : user ? (
              <>
                {/* Admin group */}
                {isAdmin && (
                  <div className="hidden md:flex items-center gap-3">
                    <a
                      href="/admin"
                      className="text-[var(--neon-accent)] hover:text-white transition-colors tracking-widest uppercase flex items-center gap-1"
                    >
                      Dashboard
                    </a>
                    <div className="w-px h-3 bg-white/20 flex-shrink-0" aria-hidden="true" />
                    <AdminUserStats />
                    <div className="w-px h-3 bg-white/20 flex-shrink-0" aria-hidden="true" />
                  </div>
                )}

                {/* Account group */}
                <div className="flex items-center gap-3">
                  <span className="truncate max-w-[200px] hidden sm:inline">{user.email}</span>
                  <Link href="/account" className="hover:text-[var(--neon-orange)] transition-colors">
                    {navT('account')}
                  </Link>
                  <button
                    onClick={() => logout()}
                    className="flex items-center gap-1 hover:text-[var(--neon-orange)] transition-colors"
                  >
                    <LogOutIcon className="w-3 h-3" />
                    {navT('logout')}
                  </button>
                </div>
              </>
            ) : (
              <div className="flex items-center gap-3">
                <Link href="/login" className="hover:text-[var(--neon-orange)] transition-colors">
                  {navT('login', { fallback: 'Login' })}
                </Link>
                <span className="opacity-30">|</span>
                <Link
                  href="/create-account"
                  className="hover:text-[var(--neon-orange)] transition-colors"
                >
                  {navT('register', { fallback: 'Register' })}
                </Link>
              </div>
            )}
          </div>
        </div>

        <div className="w-full border-b border-white/20 bg-white/5 dark:bg-black/20 backdrop-blur-xl shadow-lg relative">
          <nav className="px-2 sm:px-4 lg:px-8 2xl:px-12 flex items-center justify-between h-20">
            {/* 1. Left: Hamburger menu icon */}
            <div className="lg:hidden flex-shrink-0 min-w-[40px] flex items-center">
              <Suspense fallback={null}>
                <MobileMenu menu={menu} />
              </Suspense>
            </div>

            {/* 2. Center: Logo image ("StammBaum") */}
            <div className="flex-1 lg:flex-none flex justify-center lg:justify-start items-center h-full overflow-hidden text-center lg:overflow-visible lg:mr-6">
              <Link
                href="/"
                className="relative flex justify-center lg:justify-start items-center h-full w-full max-w-[160px] sm:max-w-[200px] lg:max-w-none lg:w-48 lg:-ml-6"
              >
                <Image
                  src="/stamm-baum-logo.png"
                  alt="Stammbaum Logo"
                  width={400}
                  height={120}
                  className="h-16 sm:h-20 lg:h-36 w-auto transition-all object-contain lg:absolute lg:left-0 lg:top-1/2 lg:-translate-y-1/2 lg:max-w-none"
                  priority
                />
              </Link>
            </div>

            {/* Center: Navigation or Search */}
            <div className="hidden lg:flex absolute left-[52%] -translate-x-1/2 h-full items-center">
              {/* Navigation - hidden when search is open */}
              <div
                className={`transition-all duration-300 ease-out ${
                  isSearchOpen
                    ? 'opacity-0 blur-sm pointer-events-none absolute'
                    : 'opacity-100 blur-0'
                }`}
              >
                <HeaderNav header={header} />
              </div>

              {/* Search - shown when search is open */}
              <div
                className={`flex items-center gap-4 transition-all duration-300 ease-out ${
                  isSearchOpen
                    ? 'opacity-100 blur-0 w-[600px]'
                    : 'opacity-0 blur-sm pointer-events-none absolute w-0'
                }`}
              >
                <Search className="flex-1" autoFocus={isSearchOpen} onSelect={() => setIsSearchOpen(false)} />
                <button
                  onClick={() => setIsSearchOpen(false)}
                  className="p-2.5 hover:bg-white/10 rounded-full transition-all hover:rotate-90 duration-300 group flex-shrink-0"
                  aria-label={t('close')}
                >
                  <XIcon className="h-5 w-5 text-white group-hover:text-[var(--neon-orange)]" />
                </button>
              </div>
            </div>

            {/* 3. Right: A group of icons */}
            <div className="flex-shrink-0 flex justify-end items-center gap-3 sm:gap-5 shrink-0">
              <WishlistBadgeClient locale={locale} initialCount={initialWishlistCount} />

              {/* Search toggle button — wide-desktop only. Below 2xl the MobileMenu owns
                  search (see MobileMenu.tsx); the icon was a dead no-op there. Search
                  panel needs ~600px of horizontal real estate (see below), so it stays
                  at 2xl even though the rest of the header switches to lg at HDR-2/6. */}
              <button
                onClick={() => setIsSearchOpen(!isSearchOpen)}
                className="hidden 2xl:inline-flex p-2 sm:p-3 hover:bg-white/10 rounded-full transition-all hover:scale-110 duration-200 group flex-shrink-0"
                aria-label={isSearchOpen ? t('close') : t('search')}
              >
                {isSearchOpen ? (
                  <>
                    <XIcon className="h-4 w-4 sm:h-5 sm:w-5 text-white group-hover:text-[var(--neon-orange)] 2xl:hidden" />
                    {/* Invisible icon on desktop to preserve width and prevent layout shift */}
                    <SearchIcon className="hidden 2xl:block h-5 w-5 opacity-0" />
                  </>
                ) : (
                  <SearchIcon className="h-4 w-4 sm:h-5 sm:w-5 text-white group-hover:text-[var(--neon-orange)]" />
                )}
              </button>

              {/* Fixed 44px slot: the cart modal is lazy-loaded (renders nothing until
                  ready), so reserve its space to avoid the icon popping in. */}
              <div className="h-11 w-11 flex-shrink-0 flex items-center justify-center">
                <Suspense fallback={<OpenCartButton />}>
                  <Cart />
                </Suspense>
              </div>

              <Link
                href={user ? '/account' : '/login'}
                className="h-11 w-11 flex items-center justify-center border border-muted-foreground/30 rounded-full transition-all hover:scale-110 hover:border-[var(--neon-orange)] duration-200 group flex-shrink-0"
                aria-label={navT('account')}
              >
                <UserIcon className="h-4 w-4 sm:h-5 sm:w-5 text-white group-hover:text-[var(--neon-orange)]" />
              </Link>
            </div>
          </nav>
        </div>
      </header>
      <AnnouncementBanner announcement={header.announcement} />
    </>
  )
}
